// components/NavigationBar.js
import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Drawer,
  List,
  ListItem,
  IconButton,
  useMediaQuery,
  Chip,
} from '@mui/material';
import { Menu, Close, Star, LocalOffer } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';

const NavigationBar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const menuItems = [
    { name: 'Home', badge: false, path: '/' },
    { name: 'Rooms & Suites', badge: true, path: '/RoomsSuites' },
    { name: 'Dining', badge: false, path: '#' },
    { name: 'Amenities', badge: false, path: '#' },
    { name: 'Gallery', badge: false, path: '#' },
    { name: 'Offers', badge: true, path: '#' },
    { name: 'About', badge: false, path: '#' },
    { name: 'Contact', badge: false, path: '/ContactPage' }
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActiveLink = (path) => {
    return location.pathname === path;
  };

  const drawer = (
    <Box sx={{ 
      textAlign: 'center', 
      p: 2,
      background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
      height: '100%',
      color: 'white'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography 
          variant="h5" 
          fontWeight="bold"
          component={Link}
          to="/"
          onClick={handleDrawerToggle}
          sx={{
            background: 'linear-gradient(45deg, #FFD700, #FFEC8B)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            textDecoration: 'none'
          }}
        >
          Grand Luxe
        </Typography>
        <IconButton 
          onClick={handleDrawerToggle}
          sx={{ color: 'white' }}
        >
          <Close />
        </IconButton>
      </Box>
      <List sx={{ mt: 4 }}>
        {menuItems.map((item) => (
          <ListItem key={item.name} sx={{ py: 1 }}>
            <Button 
              component={Link}
              to={item.path}
              color="inherit" 
              fullWidth
              onClick={handleDrawerToggle}
              sx={{
                justifyContent: 'flex-start',
                fontSize: '1.1rem',
                py: 2,
                borderRadius: '12px',
                background: isActiveLink(item.path) ? 'rgba(255,255,255,0.15)' : 'transparent',
                border: isActiveLink(item.path) ? '1px solid rgba(255,255,255,0.3)' : '1px solid transparent',
                '&:hover': {
                  background: 'rgba(255,255,255,0.1)',
                  transform: 'translateX(8px)',
                  border: '1px solid rgba(255,255,255,0.2)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {item.name}
              {item.badge && (
                <Chip
                  label="New"
                  size="small"
                  sx={{
                    ml: 1,
                    background: 'linear-gradient(45deg, #FF6B6B, #FF8E53)',
                    color: 'white',
                    fontSize: '0.7rem',
                    height: '20px'
                  }}
                />
              )}
            </Button>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <AppBar 
      position="sticky" 
      sx={{ 
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        color: 'text.primary',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar sx={{ 
          justifyContent: 'space-between', 
          py: 1,
          minHeight: '80px !important'
        }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography 
              variant="h4" 
              fontWeight="bold"
              component={Link}
              to="/"
              sx={{ 
                background: 'linear-gradient(45deg, #1a1a1a, #4a4a4a)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                '&:hover': {
                  background: 'linear-gradient(45deg, #4a4a4a, #1a1a1a)',
                  backgroundClip: 'text',
                }
              }}
            >
              <Star sx={{ 
                fontSize: '2rem', 
                color: '#FFD700',
                filter: 'drop-shadow(0 2px 4px rgba(255,215,0,0.3))'
              }} />
              Grand Luxe
            </Typography>
          </Box>

          {/* Desktop Menu */}
          {!isMobile && (
            <Box sx={{ 
              display: 'flex', 
              gap: 0.5,
              background: 'rgba(255,255,255,0.8)',
              borderRadius: '25px',
              padding: '8px',
              border: '1px solid rgba(0,0,0,0.1)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}>
              {menuItems.map((item) => (
                <Button 
                  key={item.name} 
                  component={Link}
                  to={item.path}
                  color="inherit"
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    px: 2,
                    py: 1,
                    borderRadius: '20px',
                    minWidth: 'auto',
                    position: 'relative',
                    background: isActiveLink(item.path) ? 'linear-gradient(45deg, #1a1a1a, #4a4a4a)' : 'transparent',
                    color: isActiveLink(item.path) ? 'white' : 'inherit',
                    '&:hover': { 
                      background: 'linear-gradient(45deg, #1a1a1a, #4a4a4a)',
                      color: 'white',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {item.name}
                  {item.badge && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -5,
                        right: 5,
                        width: 8,
                        height: 8,
                        background: 'linear-gradient(45deg, #FF6B6B, #FF8E53)',
                        borderRadius: '50%',
                        animation: 'pulse 2s infinite'
                      }}
                    />
                  )}
                </Button>
              ))}
            </Box>
          )}

          {/* CTA Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {!isMobile && (
              <Button
                component={Link}
                to="/offers"
                startIcon={<LocalOffer />}
                sx={{
                  color: '#FF6B6B',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'rgba(255,107,107,0.1)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Special Offers
              </Button>
            )}
            
            <Button 
              component={Link}
              to="/RoomsSuites"
              variant="contained" 
              size="large"
              sx={{ 
                background: 'linear-gradient(45deg, #1a1a1a, #4a4a4a)',
                borderRadius: '25px',
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #4a4a4a, #1a1a1a)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 30px rgba(0,0,0,0.3)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Book Now
            </Button>

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                sx={{
                  color: '#1a1a1a',
                  background: 'rgba(0,0,0,0.05)',
                  '&:hover': {
                    background: 'rgba(0,0,0,0.1)'
                  }
                }}
                onClick={handleDrawerToggle}
              >
                <Menu />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 320,
            border: 'none'
          },
        }}
      >
        {drawer}
      </Drawer>

      <style jsx global>{`
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.2); opacity: 0.7; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </AppBar>
  );
};

export default NavigationBar;